import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Banknote } from 'lucide-react';

const COLOR_MAP = {
  red:    { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-600',     btnBg: 'bg-red-50',     btnBorder: 'border-red-200',     btnText: 'text-red-600' },
  green:  { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600', btnBg: 'bg-emerald-50', btnBorder: 'border-emerald-200', btnText: 'text-emerald-600' },
  blue:   { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-600',    btnBg: 'bg-blue-50',    btnBorder: 'border-blue-200',    btnText: 'text-blue-600' },
  purple: { bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-600',  btnBg: 'bg-purple-50',  btnBorder: 'border-purple-200',  btnText: 'text-purple-600' },
  orange: { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-600',  btnBg: 'bg-orange-50',  btnBorder: 'border-orange-200',  btnText: 'text-orange-600' },
};

// Format a raw internal string (e.g. "10000.5") to display "10 000,50"
const fmtDisplay = (raw) => {
  if (!raw) return '';
  const dotIdx = raw.indexOf('.');
  let intPart, decPart;
  if (dotIdx >= 0) {
    intPart = raw.substring(0, dotIdx);
    decPart = raw.substring(dotIdx + 1).substring(0, 2); // max 2 decimals
  } else {
    intPart = raw;
    decPart = null;
  }
  // Format integer part with spaces
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  if (decPart !== null) {
    return formatted + ',' + decPart;
  }
  return formatted;
};

// Format a number for display in quick amounts etc.
const fmtNum = (v) => {
  if (v == null) return '';
  const s = v.toFixed(2);
  const [intP, decP] = s.split('.');
  const formatted = intP.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  if (decP && parseInt(decP) > 0) return formatted + ',' + decP;
  return formatted;
};

// Parse raw internal string to float
const toFloat = (raw) => {
  if (!raw) return 0;
  return parseFloat(raw) || 0;
};

// Sanitize raw input: keep only digits and at most one dot, max 2 decimal places
const sanitize = (s) => {
  // Replace comma with dot
  let val = s.replace(/,/g, '.');
  // Remove everything except digits and dot
  val = val.replace(/[^0-9.]/g, '');
  // Keep only first dot
  const parts = val.split('.');
  if (parts.length > 2) {
    val = parts[0] + '.' + parts.slice(1).join('');
  }
  // Limit decimal places to 2
  const dotIdx = val.indexOf('.');
  if (dotIdx >= 0 && val.length - dotIdx > 3) {
    val = val.substring(0, dotIdx + 3);
  }
  // Remove leading zeros (but keep "0" and "0.x")
  if (val.length > 1 && val[0] === '0' && val[1] !== '.') {
    val = val.replace(/^0+/, '') || '0';
  }
  return val;
};

const AmountInput = ({
  value = 0,
  onChange,
  accentColor = 'red',
  currency = 'DA',
  quickAmounts = [5000, 10000, 50000],
  showKeypad = true,
  label,
}) => {
  const inputRef = useRef(null);
  const [raw, setRaw] = useState('');
  const c = COLOR_MAP[accentColor] || COLOR_MAP.red;
  const skipSync = useRef(false);

  // Sync external value to internal raw (only when not actively editing)
  useEffect(() => {
    if (skipSync.current) {
      skipSync.current = false;
      return;
    }
    if (value === 0 || value == null) {
      setRaw('');
    } else {
      // Convert float to raw string: 10000.5 -> "10000.5"
      const s = Number(value).toString();
      setRaw(s);
    }
  }, [value]);

  const push = useCallback((num) => {
    skipSync.current = true;
    onChange(num);
  }, [onChange]);

  // --- Keyboard input handler ---
  const handleInputChange = (e) => {
    // Strip spaces (display formatting), keep digits, dots, commas
    const stripped = e.target.value.replace(/\s/g, '');
    const cleaned = sanitize(stripped);
    setRaw(cleaned);
    push(toFloat(cleaned));
  };

  const handleKeyDown = (e) => {
    const allowed = ['Backspace','Delete','Tab','Escape','Enter','ArrowLeft','ArrowRight','Home','End'];
    if (allowed.includes(e.key)) return;
    if ((e.ctrlKey || e.metaKey) && ['a','c','v','x'].includes(e.key.toLowerCase())) return;
    // Allow digits, dot, comma
    if (/^[0-9.,]$/.test(e.key)) {
      // Block second decimal
      if ((e.key === '.' || e.key === ',') && raw.includes('.')) {
        e.preventDefault();
      }
      return;
    }
    e.preventDefault();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData).getData('text');
    const cleaned = sanitize(pasted);
    if (cleaned) {
      setRaw(cleaned);
      push(toFloat(cleaned));
    }
  };

  // --- Keypad handlers ---
  const appendToRaw = (str) => {
    const newRaw = sanitize(raw + str);
    setRaw(newRaw);
    push(toFloat(newRaw));
    inputRef.current?.focus();
  };

  const keypadDigit = (digit) => appendToRaw(String(digit));
  const keypadZero = () => appendToRaw('0');
  const keypadDecimal = () => {
    if (!raw.includes('.')) {
      const newRaw = raw ? raw + '.' : '0.';
      setRaw(newRaw);
      push(toFloat(newRaw));
      inputRef.current?.focus();
    }
  };
  const keypadBackspace = () => {
    const newRaw = raw.slice(0, -1);
    setRaw(newRaw);
    push(toFloat(newRaw));
    inputRef.current?.focus();
  };

  const handleClear = () => {
    setRaw('');
    push(0);
    inputRef.current?.focus();
  };

  const handleQuickAdd = (amt) => {
    const num = Math.round(((value || 0) + amt) * 100) / 100;
    const s = num.toString();
    setRaw(s);
    push(num);
    inputRef.current?.focus();
  };

  const displayValue = fmtDisplay(raw);

  return (
    <div className="space-y-4" data-testid="amount-input-container">
      {label && <label className="block text-sm font-semibold text-gray-700 uppercase">{label}</label>}

      {/* Main input */}
      <div
        className={`rounded-2xl p-5 text-center cursor-text border-2 transition-all ${c.bg} ${c.border}`}
        onClick={() => inputRef.current?.focus()}
      >
        <div className="flex items-baseline justify-center gap-2">
          <input
            ref={inputRef}
            data-testid="amount-input-field"
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Saisir un montant"
            className={`text-center text-4xl sm:text-5xl font-bold bg-transparent border-none outline-none w-full max-w-xs caret-current ${
              raw ? 'text-gray-900' : 'text-gray-400'
            }`}
            style={{ caretColor: 'currentColor' }}
          />
          <span className="text-xl font-bold text-gray-400 flex-shrink-0">{currency}</span>
        </div>
      </div>

      {/* Quick amounts */}
      {quickAmounts.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {quickAmounts.map((amt) => (
            <button key={amt} type="button" onClick={() => handleQuickAdd(amt)}
              className="py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 font-semibold hover:bg-gray-50 transition-all">
              +{fmtNum(amt)}
            </button>
          ))}
        </div>
      )}

      {/* Clear */}
      <button type="button" onClick={handleClear}
        className={`w-full py-3 ${c.btnBg} border-2 ${c.btnBorder} rounded-xl ${c.btnText} font-semibold hover:opacity-80 transition-all`}>
        Effacer
      </button>

      {/* Helper */}
      <div className="flex items-center gap-2 text-gray-400 text-sm justify-center">
        <Banknote size={16} />
        <span>Tapez au clavier ou utilisez le pavé</span>
      </div>

      {/* Keypad */}
      {showKeypad && (
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button key={num} type="button" onClick={() => keypadDigit(num)}
              className="py-5 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-2xl font-semibold hover:bg-gray-50 transition-all active:bg-gray-100">
              {num}
            </button>
          ))}
          <button type="button" onClick={keypadDecimal}
            className={`py-5 border-2 rounded-xl text-2xl font-semibold transition-all active:bg-gray-100 ${
              raw.includes('.') ? 'bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed' : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'
            }`}
            disabled={raw.includes('.')}
          >,</button>
          <button type="button" onClick={keypadZero}
            className="py-5 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-2xl font-semibold hover:bg-gray-50 transition-all active:bg-gray-100">0</button>
          <button type="button" onClick={keypadBackspace}
            className="py-5 bg-white border-2 border-gray-200 rounded-xl text-gray-900 text-2xl font-semibold hover:bg-gray-50 transition-all active:bg-gray-100">&#x232B;</button>
        </div>
      )}
    </div>
  );
};

export default AmountInput;
